import { MediaType, useSearchFilesInfinite } from '@qse/api'
import { type FileItem, transformApiFiles, usePageFocusRefetch } from '@qse/utils'
import { useCallback, useMemo } from 'react'

type SortKey = 'name' | 'size' | 'date' | 'trashedDate'
type SortOrder = 'ASC' | 'DESC'

const SORT_OPTIONS = [
  { key: 'name', label: 'Name', sortBy: 'name' as const },
  { key: 'size', label: 'Size', sortBy: 'sizeBytes' as const },
  { key: 'date', label: 'Date Created', sortBy: 'createdAt' as const },
]

const TRASH_SORT_OPTIONS = [
  { key: 'name', label: 'Name', sortBy: 'name' as const },
  { key: 'trashedDate', label: 'Trashed Date', sortBy: 'trashedAt' as const },
]

interface UseFileExplorerDataProps {
  searchParams?: {
    limit?: number
    offset?: number
    parentPath?: string
    parentId?: string
    search?: string
    mediaType?: MediaType
    sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
    sortOrder?: 'ASC' | 'DESC'
    location?: 'HOME' | 'FAVORITE' | 'TRASH'
  }
  externalFiles?: FileItem[]
  externalIsLoading?: boolean
  externalError?: Error | null
  externalOnRetry?: () => void
  externalHasNextPage?: boolean
  externalIsFetchingNextPage?: boolean
  externalOnLoadMore?: () => void
  sortOption: SortKey
  sortOrder: SortOrder
  trashMode?: boolean
}

export const useFileExplorerData = ({
  searchParams,
  externalFiles,
  externalIsLoading,
  externalError,
  externalOnRetry,
  externalHasNextPage,
  externalIsFetchingNextPage,
  externalOnLoadMore,
  sortOption,
  sortOrder,
  trashMode = false,
}: UseFileExplorerDataProps) => {
  // Map frontend sort options to backend API parameters
  const getSortParams = useCallback(
    (sortKey: SortKey, order: SortOrder) => {
      const options = trashMode ? TRASH_SORT_OPTIONS : SORT_OPTIONS
      const option = options.find(opt => opt.key === sortKey)

      return {
        sortBy: option?.sortBy || 'name',
        sortOrder: order,
      }
    },
    [trashMode],
  )

  // Default search parameters with current sort state
  const defaultSearchParams = useMemo(() => {
    const sortParams = getSortParams(sortOption, sortOrder)

    return {
      ...sortParams,
      ...searchParams,
      // Ensure all numeric values are converted to strings for API compatibility
      limit: searchParams?.limit || 10,
      offset: searchParams?.offset,
    }
  }, [searchParams, sortOption, sortOrder, getSortParams])

  // Fetch files from API if no external files provided and no external states
  const {
    data: infiniteData,
    isLoading: internalIsLoading,
    isFetching: internalIsFetching,
    error: internalError,
    refetch: internalRefetch,
    fetchNextPage: internalFetchNextPage,
    hasNextPage: internalHasNextPage,
    isFetchingNextPage: internalIsFetchingNextPage,
  } = useSearchFilesInfinite(defaultSearchParams, {
    enabled: !externalFiles,
  })

  // Handle page focus refetch for FileExplorer data
  const handlePageFocusRefetch = useCallback(() => {
    if (!externalFiles) {
      // Only refetch if we're using internal API calls (not external files)
      internalRefetch()
    }
  }, [externalFiles, internalRefetch])

  usePageFocusRefetch(handlePageFocusRefetch)

  // Use external states if provided, otherwise use internal states
  const isLoading = externalIsLoading ?? internalIsLoading
  const isFetching = internalIsFetching // Always use internal isFetching since external doesn't provide it
  const error = externalError ?? internalError
  const hasNextPage = externalHasNextPage ?? internalHasNextPage
  const isFetchingNextPage = externalIsFetchingNextPage ?? internalIsFetchingNextPage
  const onRetry = externalOnRetry ?? internalRefetch
  const onLoadMore = externalOnLoadMore ?? internalFetchNextPage

  // Use all loaded files from all pages (append)
  const files = useMemo(() => {
    if (infiniteData?.pages && infiniteData.pages.length > 0) {
      // APPEND all files from all loaded pages
      return infiniteData.pages.flatMap(page => transformApiFiles(page))
    }

    return []
  }, [infiniteData])

  return {
    files,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    isFetchingNextPage,
    onRetry,
    onLoadMore,
    defaultSearchParams,
  }
}
